import React, { useEffect, useLayoutEffect, useRef, useState } from "react"; // Import useLayoutEffect
import SelfView from "../selfView/SelfView";
import { CircularProgress, Fab, Typography } from "@mui/material";
import { useCall } from "../../call-sdk/context/CallContext";
import { useNavigate } from "react-router-dom";
import { useTwilioUserMediaContext } from "src/call-sdk/context/TwilioUserMediaContext";
import {
  ConnectingContainer,
  Container,
  EndButtonContainer,
  QueueTextContainer,
} from "../connecting/ConnectingStyles";
import cancelIcon from "../../assets/Room/end-call.svg";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { useToast } from "src/context/ToastContext";
import { useRoomContext } from "src/call-sdk/context/RoomContext";
import ErrorBoundary from "../ErrorBoundary";
import EventTracker from "src/pendo/EventTracker";
import StorageConstants from "src/config/storage.constants";
import LocalStorage from "src/call-sdk/lib/local-storage";
import useLogout from "src/hooks/useLogout";
import AddinCallIcon from "../../assets/Room/add-call.svg";
import HuQRDisplay from "../HuQRDisplay/HuQRDisplay";
import useQRHuEnabled from "src/hooks/useQRHuEnabled";
import * as Sentry from "@sentry/react";

const Connecting = () => {
  const {
    business,
    getVideoInterpreter,
    isJoining,
    join,
    isCallRejected,
    reject,
    connected,
    roomName,
    error,
    apiErrorFlag,
    twilioVideoToken,
    region,
    isWebTokenExpired,
    position,
    joinAudioCall,
    setErrorMessage,
  } = useCall();

  const { connectToRoom, room, connecting } = useRoomContext();

  const [showHuQRDisplay, setShowHuQRDisplay] = useState(false);

  const navigate = useNavigate();
  const interpreterFetchedRef = useRef(false);
  const { destroyMedia } = useTwilioUserMediaContext();
  const toast = useToast();

  const logout = useLogout();

  const isHUUser = Boolean(LocalStorage.get(StorageConstants.IS_HU_USER));

  const qrHUConnectEnabled = useQRHuEnabled(business);

  useLayoutEffect(() => {
    if (!interpreterFetchedRef.current && isHUUser) {
      const requestId = LocalStorage.get(StorageConstants.REQUEST_ID);
      joinAudioCall({ requestId });
      interpreterFetchedRef.current = true;
      return;
    }

    if (!interpreterFetchedRef.current && business?.phoneNumber) {
      getVideoInterpreter(business.phoneNumber);
      interpreterFetchedRef.current = true;
    }

    if (!isHUUser && !interpreterFetchedRef.current && !business?.phoneNumber) {
      setErrorMessage(
        "Something went wrong. Please scan the QR code again and then click Start."
      );
      navigate({ pathname: "/qr-error" }, { replace: true });
    }
  }, [
    business?.phoneNumber,
    getVideoInterpreter,
    isHUUser,
    joinAudioCall,
    navigate,
    setErrorMessage,
  ]);

  useEffect(() => {
    if (isCallRejected) {
      console.log(room?.state);
      room?.disconnect();
      destroyMedia();
      if (isHUUser) {
        logout().then(() => {});
        navigate({ pathname: "/qr-error" }, { replace: true });
      } else {
        navigate({ pathname: "/qr-business" }, { replace: true });
      }
    }
  }, [isCallRejected, navigate, room, isHUUser, logout, destroyMedia]);

  useEffect(() => {
    if (isJoining && roomName) {
      join();
    }
  }, [isJoining, roomName, join]);

  useEffect(() => {
    if (error && error?.length > 0 && apiErrorFlag) {
      destroyMedia();
      Sentry.captureMessage(`Error Obtained on connecting screen - ${error}`);

      if (isWebTokenExpired) {
        navigate({ pathname: `/qr-reverify` }, { replace: true });
      } else if (isHUUser) {
        navigate({ pathname: "/qr-error" }, { replace: true });
      } else {
        navigate({ pathname: "/qr-business" }, { replace: true });
        toast.error(error);
      }
    }
  }, [
    error,
    apiErrorFlag,
    navigate,
    toast,
    destroyMedia,
    isWebTokenExpired,
    isHUUser,
  ]);

  useEffect(() => {
    if (connected && !isJoining && twilioVideoToken && region && roomName) {
      setTimeout(() => {
        connectToRoom(twilioVideoToken, region, roomName);
      }, 1000);
    }
  }, [connected, isJoining, connectToRoom, twilioVideoToken, region, roomName]);

  useEffect(() => {
    if (!connecting && room?.state === "connected") {
      setTimeout(() => {
        navigate({ pathname: "/qr-room" }, { replace: true });
      }, 1500);
    }
  }, [room, navigate, connecting]);

  const handleRejectCall = () => {
    reject(isHUUser);

    EventTracker.trackEvent("cancelCallFromQueue", { position: position });
  };

  return (
    <ErrorBoundary>
      <Container>
        {!isHUUser && <SelfView />}
        <ConnectingContainer className={isHUUser ? "hu-view-container" : ""}>
          <CircularProgress size={40} color="success" thickness={4} />
          <QueueTextContainer>
            {isHUUser ? (
              <p aria-label="Connecting">Connecting...</p>
            ) : (
              <p aria-label="Please wait for the next available interpreter">
                Please wait for the next available interpreter...
              </p>
            )}
          </QueueTextContainer>
        </ConnectingContainer>
        <EndButtonContainer>
          {!isHUUser && qrHUConnectEnabled && (
            <div>
              <Fab
                style={{
                  opacity: 0.3,
                  backgroundColor: `${
                    showHuQRDisplay ? "rgba(0, 170, 85, 0.6)" : "#1D1D1D"
                  }`,
                }}
                className="cancelBtn"
                onClick={() => setShowHuQRDisplay(true)}
                aria-label="Open Audio Connect"
                disabled
              >
                <LazyLoadImage src={AddinCallIcon} />
              </Fab>
              <Typography className="end-text">Connect</Typography>
            </div>
          )}
          {/* <Fab className="normalBtn">
          <VideocamIcon
            sx={{
              color: "black",
            }} />
        </Fab>
        <Fab className="normalBtn">
          <MicIcon
            sx={{
              color: "black",
            }} />
        </Fab> */}
          <div>
            <Fab
              className="cancelBtn"
              onClick={handleRejectCall}
              aria-label="Cancel call"
            >
              {/* <CloseIcon
            sx={{
              color: "white",

            }} /> */}
              <LazyLoadImage src={cancelIcon} />
            </Fab>
            <Typography className="end-text">End</Typography>
          </div>
        </EndButtonContainer>
        <HuQRDisplay
          open={showHuQRDisplay}
          onClose={() => setShowHuQRDisplay(false)}
        />
      </Container>
    </ErrorBoundary>
  );
};

export default Connecting;
